import * as Sentry from '@sentry/react';
import { shuffle } from 'lodash-es';
import { proxy, useSnapshot } from 'valtio';
import { z } from 'zod';

import type { PlaylistInput } from '@graphql/reactQuery';
import { PlaylistType } from '@graphql/reactQuery';

/**
 * Manages sequence of tracks to be played in audio player
 * playbackTrack IDs stores the IDs of tracks to be played in order
 * active holds the index and ID of the currently playing track
 * The tracks between active index and active index + queue length will appear as the "Queue" in the UI. Adding tracks appends to this section
 * After the active index + queue length is the threshold where the "Up Next" section would appear in the UI, generally being a curated playlist
 *
 * Example breakdown:
 * active index of 2, nextUpThreshold of 5 for [0, 1, 2, 3, 4, 5, 6, 7, 8]
 * tracks 0-1 have already been played
 * tracks 3-4 are in the queue
 * tracks 5-8 are "up next"
 */

const PlaylistInputSchema = z.union([
  z.object({
    type: z.literal(PlaylistType.Home),
    associationId: z
      .null()
      .optional()
      // We always want to give a null instead of undefined to the API
      .transform(() => null),
  }),
  z.object({
    type: z.union([z.literal(PlaylistType.Holder), z.literal(PlaylistType.Artist)]),
    associationId: z.string(),
  }),
]);

export const AudioQueueSchema = z.object({
  /** List of tracks up next in original order to allow for unshuffling (e.g. the fetched playlist) */
  upNextTrackIds: z.array(z.string()),
  /** List of tracks in playback order */
  playbackTrackIds: z.array(z.string()),
  /** Active track information */
  active: z.union([
    z.object({
      /** ID of currently active track */
      trackId: z.string(),
      /** Index of currently active track */
      playbackIndex: z.number(),
    }),
    z.null(),
  ]),
  /** Whether or not shuffle is turned on */
  shuffleEnabled: z.boolean(),
  playlistInput: z.optional(z.nullable(PlaylistInputSchema)),
  /** Length of the queue */
  queueLength: z.number(),
});

type AudioQueueType = (typeof AudioQueueSchema)['_output'];

const initialAudioQueue: AudioQueueType = {
  upNextTrackIds: [],
  playbackTrackIds: [],
  active: null,
  shuffleEnabled: false,
  queueLength: 0,
};

export const AudioQueue = proxy(initialAudioQueue);

export const setActiveTrack = (index: number) => {
  const activeTrackId = AudioQueue.playbackTrackIds[index];
  if (!activeTrackId) return;

  const prevIndex = AudioQueue.active?.playbackIndex ?? 0;
  if (AudioQueue.queueLength > 0) {
    if (index < prevIndex) {
      const delta = prevIndex - index;
      const deltaTrackIds = AudioQueue.playbackTrackIds.slice(index, prevIndex);
      AudioQueue.playbackTrackIds.splice(index, delta);
      AudioQueue.playbackTrackIds.splice(index + AudioQueue.queueLength + 1, 0, ...deltaTrackIds);
    } else if (index > prevIndex) {
      const delta = index - prevIndex;
      if (delta >= AudioQueue.queueLength) {
        AudioQueue.queueLength = 0;
      } else {
        AudioQueue.queueLength -= delta;
      }
    }
  }

  AudioQueue.active = {
    playbackIndex: index,
    trackId: activeTrackId,
  };
};

export const addTrackToQueue = (...tracks: { trackId: string; position?: number }[]) => {
  let queueLengthDelta = 0;
  for (const { trackId, position } of tracks) {
    if (typeof position === 'undefined') {
      // Append to the end by default if no position provided
      AudioQueue.playbackTrackIds.splice(
        (AudioQueue.active?.playbackIndex ?? 0) + AudioQueue.queueLength + 1,
        0,
        trackId,
      );
    } else if (position >= 0 && position <= AudioQueue.playbackTrackIds.length) {
      AudioQueue.playbackTrackIds.splice(position, 0, trackId);
    }
    queueLengthDelta += 1;

    if (AudioQueue.active && position && position < AudioQueue.active.playbackIndex)
      AudioQueue.active.playbackIndex += 1;
  }

  if (AudioQueue.active === null) {
    setActiveTrack(0);
    queueLengthDelta -= 1;
  }
  AudioQueue.queueLength += queueLengthDelta;
};

export const removeTrackAtPosition = (position: number) => {
  if (position < 0 || position >= AudioQueue.playbackTrackIds.length) return;
  AudioQueue.playbackTrackIds.splice(position, 1);

  if (AudioQueue.active && position < AudioQueue.active.playbackIndex) {
    AudioQueue.active.playbackIndex -= 1;
  }

  if (
    AudioQueue.active &&
    position > AudioQueue.active.playbackIndex &&
    position <= AudioQueue.active.playbackIndex + AudioQueue.queueLength
  ) {
    AudioQueue.queueLength -= 1;
  }

  if (AudioQueue.playbackTrackIds.length === 0) {
    AudioQueue.active = null;
  }
};

export const removeTrackById = (trackId: string) => {
  const position = AudioQueue.playbackTrackIds.indexOf(trackId);
  removeTrackAtPosition(position);
};

export const getQueueTrackIds = () => {
  if (AudioQueue.queueLength === 0 || !AudioQueue.active) return [];
  const playbackIndex = AudioQueue.active?.playbackIndex ?? 0;
  return AudioQueue.playbackTrackIds.slice(
    playbackIndex + 1,
    playbackIndex + AudioQueue.queueLength + 1,
  );
};

export const getUpNextTrackIds = () => {
  if (!AudioQueue.active) return [];
  return AudioQueue.playbackTrackIds.slice(
    AudioQueue.active.playbackIndex + AudioQueue.queueLength + 1,
  );
};

export const clearQueue = () => {
  if (AudioQueue.queueLength === 0) return;
  const playbackIndex = AudioQueue.active?.playbackIndex ?? 0;
  AudioQueue.playbackTrackIds.splice(playbackIndex + 1, AudioQueue.queueLength);
  AudioQueue.queueLength = 0;
};

const changeUpNextTrackIds = (trackIds: string[]) => {
  const upNextTracksLength = getUpNextTrackIds().length;
  const playbackIndex = AudioQueue.active?.playbackIndex ?? 0;
  const spliceIndex = playbackIndex + AudioQueue.queueLength + 1;
  AudioQueue.playbackTrackIds.splice(spliceIndex, upNextTracksLength, ...trackIds);
};

export const setUpNextTrackIds = (trackIds: string[]) => {
  AudioQueue.upNextTrackIds = [...trackIds];
  changeUpNextTrackIds(trackIds);
  if (AudioQueue.shuffleEnabled) shuffleTracks();
};

export const appendUpNextTrackIds = () => {
  const trackIdsToAppend = AudioQueue.shuffleEnabled
    ? shuffle(AudioQueue.upNextTrackIds)
    : AudioQueue.upNextTrackIds;
  AudioQueue.playbackTrackIds.splice(AudioQueue.playbackTrackIds.length, 0, ...trackIdsToAppend);
};

export const goToNextTrack = () => {
  if (!AudioQueue.active) return;

  if (AudioQueue.active.playbackIndex < AudioQueue.playbackTrackIds.length - 1) {
    setActiveTrack(AudioQueue.active.playbackIndex + 1);
  }
};

export const goToPrevTrack = () => {
  if (!AudioQueue.active) return;

  if (AudioQueue.active.playbackIndex > 0) {
    setActiveTrack(AudioQueue.active.playbackIndex - 1);
  }
};

const shuffleTracks = () => {
  changeUpNextTrackIds(shuffle(AudioQueue.upNextTrackIds));
};

const unshuffleTracks = () => {
  changeUpNextTrackIds([...AudioQueue.upNextTrackIds]);
};

export const setShuffleEnabled = (shuffleEnabled: boolean) => {
  AudioQueue.shuffleEnabled = shuffleEnabled;
  if (shuffleEnabled) {
    shuffleTracks();
  } else {
    unshuffleTracks();
  }
};

export const toggleShuffleEnabled = () => {
  setShuffleEnabled(!AudioQueue.shuffleEnabled);
};

export const homePlaylist: PlaylistInput = { type: 'HOME' };

export function setPlaylistInput(playlist: PlaylistInput) {
  const parsedPlaylist = PlaylistInputSchema.safeParse(playlist);

  if (!parsedPlaylist.success) {
    Sentry.captureException(parsedPlaylist.error, {
      level: 'warning',
    });
    // eslint-disable-next-line no-console
    console.warn(parsedPlaylist.error);
    AudioQueue.playlistInput = {
      type: 'HOME',
      associationId: null,
    };
    return;
  }
  if (
    parsedPlaylist.data.type !== AudioQueue.playlistInput?.type ||
    parsedPlaylist.data.associationId !== AudioQueue.playlistInput.associationId
  ) {
    AudioQueue.playlistInput = parsedPlaylist.data;
  }
}

export const clearPlaybackSequence = () => {
  AudioQueue.queueLength = 0;
  AudioQueue.playbackTrackIds = [];
  AudioQueue.upNextTrackIds = [];
};

export const useAudioQueue = () => useSnapshot(AudioQueue);

// Just used for tests
export const testSetPlaybackTrackIds = (trackIds: string[]) => {
  AudioQueue.playbackTrackIds = [...trackIds];
  setActiveTrack(0);
  if (AudioQueue.shuffleEnabled) shuffleTracks();
};

// Just used for tests
export const testReset = () => {
  const { active, playbackTrackIds, playlistInput, queueLength, shuffleEnabled, upNextTrackIds } =
    initialAudioQueue;
  AudioQueue.active = active;
  AudioQueue.playbackTrackIds = playbackTrackIds;
  AudioQueue.playlistInput = playlistInput;
  AudioQueue.queueLength = queueLength;
  AudioQueue.shuffleEnabled = shuffleEnabled;
  AudioQueue.upNextTrackIds = upNextTrackIds;
};
